import { config } from '@/config';
import { generateRecaptchaTokenSafe } from '@/lib/recaptcha';
import type { RecaptchaAction } from '@/types/recaptcha';
import { resolveAiApiRoot, type AiRoutingContext } from './aiHostResolver';

export interface EventResponse {
  eventType: string;
  payload: unknown;
}

export type { AiRoutingContext };

let _identityReadyResolve: (() => void) | null = null;
const _identityReady = new Promise<void>((resolve) => {
  _identityReadyResolve = resolve;
});

export function awaitIdentityReady(): Promise<void> {
  return _identityReady;
}

async function getFetchResponse(
  endpoint: string,
  options: RequestInit = {},
  recaptchaAction?: RecaptchaAction,
  routingContext?: AiRoutingContext,
  _isRetry = false
) {
  const API_ROOT = resolveAiApiRoot(config.cbAiApiUrl, routingContext);
  const url = `${API_ROOT}${endpoint}`;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-cb-consumer-app-name': 'api-explorer',
  };

  // Add reCAPTCHA token if action is provided
  // Note: Generate fresh token for each request (no caching) since tokens can only be used once
  if (recaptchaAction) {
    try {
      const recaptchaToken = await generateRecaptchaTokenSafe(
        recaptchaAction,
        { useCache: false } // Disable caching - tokens are single-use
      );
      if (recaptchaToken) {
        defaultHeaders['x-recaptcha-token'] = recaptchaToken;
      }
    } catch (error) {
      // Log error but don't fail the request
      console.warn('Failed to generate reCAPTCHA token:', error);
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (
    response.status === 401 &&
    !_isRetry &&
    endpoint !== '/api/public/api_explorer/identity'
  ) {
    await fetch(`${API_ROOT}/api/public/api_explorer/identity`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-cb-consumer-app-name': 'api-explorer' },
    }).catch(() => {});
    return getFetchResponse(
      endpoint,
      options,
      recaptchaAction,
      routingContext,
      true
    );
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'An unknown error occurred' }));

    // Handle reCAPTCHA verification failures with a special error code
    if (
      error.code === 'RECAPTCHA_VERIFICATION_FAILED' ||
      error.code === 'RECAPTCHA_TOKEN_MISSING'
    ) {
      const recaptchaError = new Error(
        error.message || 'Request verification failed'
      ) as Error & { code: string };
      recaptchaError.code = error.code;
      throw recaptchaError;
    }

    throw new Error(error.message || `API error: ${response.status}`);
  }
  return response;
}

export async function fetchIdentity(
  routingContext?: AiRoutingContext
): Promise<void> {
  try {
    await getFetchResponse(
      '/api/public/api_explorer/identity',
      { method: 'POST' },
      undefined,
      routingContext
    );
  } finally {
    _identityReadyResolve?.();
  }
}

export async function fetchCbAiApi<T>(
  endpoint: string,
  options: RequestInit = {},
  recaptchaAction?: RecaptchaAction,
  routingContext?: AiRoutingContext
): Promise<T> {
  const response = await getFetchResponse(
    endpoint,
    options,
    recaptchaAction,
    routingContext
  );
  return response.json();
}

export async function postApiExplorerKvl(
  kvl: Record<string, string | boolean | number>,
  routingContext?: AiRoutingContext
): Promise<void> {
  await getFetchResponse(
    '/api/public/api_explorer/kvl',
    {
      method: 'POST',
      body: JSON.stringify({ kvl }),
    },
    undefined,
    routingContext
  );
}

/**
 * Generator function that streams data from the Chargebee AI API as SSE events
 * Yields each parsed event as it arrives in the format { eventType, payload }
 *
 * @param endpoint API endpoint path
 * @param options Fetch request options
 * @param signal Optional AbortSignal for cancellation
 * @param recaptchaAction Optional reCAPTCHA action for protected endpoints
 * @returns AsyncGenerator yielding each parsed event
 */
export async function* streamCbAiApi<T = EventResponse>(
  endpoint: string,
  options: RequestInit = {},
  signal?: AbortSignal,
  recaptchaAction?: RecaptchaAction,
  routingContext?: AiRoutingContext
): AsyncGenerator<T, void, undefined> {
  const fetchOptions = signal ? { ...options, signal } : options;

  const response = await getFetchResponse(
    endpoint,
    fetchOptions,
    recaptchaAction,
    routingContext
  );

  const reader = response.body!.getReader();

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) {
        break;
      }

      const { done, value } = await reader.read();

      if (done) {
        if (buffer.trim() && buffer.includes('event:')) {
          const events = processSSEBuffer(buffer);
          for (const eventText of events) {
            const eventResponse = parseSSEEventToResponse(eventText);
            if (eventResponse) {
              yield eventResponse as T;
            }
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      if (buffer.includes('event:') && buffer.includes('data:')) {
        const events = processSSEBuffer(buffer);

        const lastEventStart = buffer.lastIndexOf('event:');
        const lastEventComplete = hasCompleteEvent(
          buffer.substring(lastEventStart)
        );

        if (!lastEventComplete) {
          buffer = buffer.substring(lastEventStart);
        } else {
          buffer = '';
        }

        for (const eventText of events) {
          const eventResponse = parseSSEEventToResponse(eventText);
          if (eventResponse) {
            yield eventResponse as T;
          }
        }
      }
    }
  } catch (err) {
    console.error('Error processing SSE stream:', err);
    console.trace();
    throw err;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Check if a part of the buffer contains a complete event
 */
function hasCompleteEvent(eventText: string): boolean {
  return (
    eventText.includes('data:') &&
    (eventText.includes('\n\n') || eventText.endsWith('\n'))
  );
}

/**
 * Process the SSE buffer and extract complete events
 *
 * @param buffer Current buffer containing SSE text
 * @returns Array of complete event text blocks
 */
function processSSEBuffer(buffer: string): string[] {
  const parts = buffer.split(/(?=event: )/);

  return parts.filter(
    (part) => part.trim().startsWith('event:') && part.includes('data:')
  );
}

/**
 * Parse an SSE event into the { eventType, payload } format
 * Handles both JSON data and string/primitive data formats
 *
 * @param eventText Complete SSE event text
 * @returns Formatted event response or null if invalid
 */
function parseSSEEventToResponse(eventText: string): EventResponse | null {
  try {
    // Extract event type
    const eventTypeMatch = eventText.match(/^event:\s*([^\n]+)/m);
    if (!eventTypeMatch) {
      console.log('Failed to match event type pattern in: ', eventText);
      return null;
    }
    const eventType = eventTypeMatch[1].trim();

    const dataMatch = eventText.match(/data:\s*([^]*)/);
    if (!dataMatch) {
      console.log('Failed to match data pattern in: ', eventText);
      return null;
    }

    const dataString = dataMatch[1].trim();
    let payload;

    try {
      payload = JSON.parse(dataString);
    } catch {
      if (dataString === 'true') {
        payload = true;
      } else if (dataString === 'false') {
        payload = false;
      } else {
        payload = dataString.replace(/\\n/g, '\n').replace(/\\"/g, '"');
      }
    }

    return {
      eventType,
      payload,
    };
  } catch (error) {
    console.warn('Error parsing SSE event:', error, 'in event:', eventText);
    return null;
  }
}
